import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "src/@/components/ui/dialog";
import { Input } from "src/@/components/ui/input";
import { Textarea } from "src/@/components/ui/textarea";
import { Switch } from "src/@/components/ui/switch";
import { Button } from "src/@/components/ui/button";
import DatePicker from "src/@/components/shared/datePickers/DatePicker";
import { RowInput } from "src/components/forms/RowInput";
import { format, addHours } from "date-fns";
import { CALENDARIO_DESIGN as D } from "src/pages/dashboard/Calendario/Calendario.design";

const EVENT_COLORS = [
  { value: null, label: "Padrão", bg: "#7C3AED" },
  { value: "#EF4444", label: "Vermelho", bg: "#EF4444" },
  { value: "#F97316", label: "Laranja", bg: "#F97316" },
  { value: "#EAB308", label: "Amarelo", bg: "#EAB308" },
  { value: "#22C55E", label: "Verde", bg: "#22C55E" },
  { value: "#14B8A6", label: "Teal", bg: "#14B8A6" },
  { value: "#3B82F6", label: "Azul", bg: "#3B82F6" },
  { value: "#8B5CF6", label: "Violeta", bg: "#8B5CF6" },
  { value: "#EC4899", label: "Rosa", bg: "#EC4899" },
  { value: "#6B7280", label: "Cinza", bg: "#6B7280" },
];

const AddEventDialog = ({ user, isEdit, event, open, onOpenChange, onOpen, onClose, changeValue, onCreate, onEdit }) => {

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open && !isEdit && !event?.time_start) {
      const now = new Date();
      const endDateTime = addHours(now, 1);

      changeValue({ target: { name: 'startDate', value: now } });
      changeValue({ target: { name: 'endDate', value: endDateTime } });
      changeValue({ target: { name: 'time_start', value: format(now, 'HH:mm') } });
      changeValue({ target: { name: 'time_end', value: format(endDateTime, 'HH:mm') } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit]);

  const validate = () => {
    const newErrors = {};

    if (!event?.title || event.title.trim() === "") {
      newErrors.title = "O título é obrigatório.";
    }

    if (!event?.allDay) {
      if (!event?.time_start || !/^\d{2}:\d{2}$/.test(event.time_start)) {
        newErrors.time_start = "Horário inicial deve estar no formato HH:mm.";
      }

      if (!event?.time_end || !/^\d{2}:\d{2}$/.test(event.time_end)) {
        newErrors.time_end = "Horário final deve estar no formato HH:mm.";
      }

      if (event?.startDate && event?.endDate) {
        const start = new Date(event.startDate);
        const end = new Date(event.endDate);

        if (start > end) {
          newErrors.dates = "A data final deve ser maior ou igual à data inicial.";
        }

        if (start.toDateString() === end.toDateString()) {
          const [startHours, startMinutes] = (event.time_start || "00:00").split(":").map(Number);
          const [endHours, endMinutes] = (event.time_end || "00:00").split(":").map(Number);

          const startTime = startHours * 60 + startMinutes;
          const endTime = endHours * 60 + endMinutes;

          if (endTime <= startTime) {
            newErrors.time_end = "Horário final deve ser maior que o horário inicial no mesmo dia.";
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  function handleClose() {
    onClose();
    setErrors({});
  }

  async function handleCreate() {
    if (validate()) {
      onCreate();
      handleClose();
    }
  }

  async function handleEdit() {
    if (validate()) {
      onEdit();
      handleClose();
    }
  }

  const tabs = [
    { id: "simple_event", label: "Evento Simples" },
    { id: "taks", label: "Tarefa" },
    { id: "schedule", label: "Agendamento de horários" },
  ];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={D.dialog.content}>
        <DialogHeader>
          <DialogTitle>{!isEdit ? "Novo evento" : "Editar evento"}</DialogTitle>
        </DialogHeader>

        <div className={D.dialog.divider} />

        <div className={D.dialog.section}>
          {/* Event type tabs */}
          <div className={D.dialog.tabGroup}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={event?.event_type === tab.id ? D.dialog.tabActive : D.dialog.tabInactive}
                onClick={() => changeValue({ target: { name: "event_type", value: tab.id } })}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Title */}
          <div className="w-1/2">
            <RowInput
              label="Título"
              isRequired
              input={
                <Input
                  value={event?.title}
                  name="title"
                  onChange={changeValue}
                  placeholder="Adicionar título"
                />
              }
            />
          </div>

          {/* Date and Time */}
          <div className="flex flex-row gap-2 items-center">
            <div>
              <DatePicker
                label="Data Inicial"
                selectedDate={event?.startDate || new Date()}
                name="startDate"
                onSelect={(value) => changeValue({ target: { name: "startDate", value } })}
              />
            </div>
            {!event?.allDay && (
              <div className="flex flex-row gap-2">
                <div className="w-24">
                  <label className={D.dialog.label}>hora inicial</label>
                  <input
                    type="time"
                    value={event?.time_start}
                    name="time_start"
                    onChange={changeValue}
                    className={D.dialog.timeInput}
                  />
                </div>
              </div>
            )}

            <div>
              <DatePicker
                label="Data Final"
                selectedDate={event?.endDate || new Date()}
                name="endDate"
                onSelect={(value) => changeValue({ target: { name: "endDate", value } })}
              />
            </div>

            {!event?.allDay && (
              <div className="w-24">
                <label className={D.dialog.label}>Final</label>
                <input
                  type="time"
                  name="time_end"
                  value={event?.time_end}
                  onChange={changeValue}
                  className={D.dialog.timeInput}
                />
              </div>
            )}
          </div>

          {/* All Day */}
          <div className="flex items-center space-x-2">
            <Switch
              name="allDay"
              checked={event?.allDay}
              onCheckedChange={(value) => changeValue({ target: { name: "allDay", value } })}
            />
            <label className="text-sm text-gray-700">Dia inteiro</label>
          </div>

          {/* Description */}
          <div>
            <label className={D.dialog.label}>Descrição</label>
            <Textarea
              className="w-1/2"
              name="description"
              value={event?.description}
              onChange={changeValue}
              placeholder="Descrição"
            />
          </div>

          {/* Color Picker */}
          <div className={D.colorPicker.container}>
            <label className={D.colorPicker.label}>Cor do evento</label>
            <div className={D.colorPicker.grid}>
              {EVENT_COLORS.map((c) => (
                <button
                  key={c.label}
                  type="button"
                  title={c.label}
                  className={
                    (event?.color || null) === c.value
                      ? D.colorPicker.swatchActive
                      : D.colorPicker.swatch
                  }
                  style={{ backgroundColor: c.bg }}
                  onClick={() => changeValue({ target: { name: "color", value: c.value } })}
                />
              ))}
            </div>
          </div>

          {/* Validation errors */}
          <div>
            {errors.title && <p className="text-red-500 text-sm">{errors.title}</p>}
            {errors.time_start && <p className="text-red-500 text-sm">{errors.time_start}</p>}
            {errors.dates && <p className="text-red-500 text-sm">{errors.dates}</p>}
            {errors.time_end && <p className="text-red-500 text-sm">{errors.time_end}</p>}
          </div>
        </div>

        <div className={D.dialog.divider} />

        {/* Actions */}
        <div className={D.dialog.footer}>
          <Button onClick={handleClose} variant="outline">
            Cancelar
          </Button>
          <Button onClick={isEdit ? handleEdit : handleCreate}>
            {!isEdit ? "Criar evento" : "Editar evento"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddEventDialog;
